import React from "react";
import { Helmet } from "react-helmet-async";

export default function BaumeShop() {
	const titlePage =
		"Baume Shop | Boutique e-commerce bien-être féminin par univers | Weblax Studio";
	const description =
		"Weblax a conçu Baume Shop, une boutique e-commerce bien-être féminin organisée par univers thématiques. Architecture produit repensée, UX émotionnelle, SEO optimisé et +55% de conversion. Découvrez ce projet e-commerce sur mesure.";
	const url = "https://weblax.fr/realisations/baume-shop";

	const features = [
		{
			icon: "🌿",
			title: "Navigation par univers",
			desc: "Chaque univers produit (soin, relaxation, énergie, équilibre) est conçu comme une expérience à part entière — visuelle, narrative et cohérente — pour que chaque utilisatrice se retrouve dans ce qui lui correspond.",
		},
		{
			icon: "🎯",
			title: "Filtres par intention",
			desc: "Plutôt que de filtrer par catégorie, les utilisatrices filtrent par besoin : 'Je veux me ressourcer', 'Je cherche à me détendre'. Une navigation guidée par l'émotion, pas par la logique produit.",
		},
		{
			icon: "📱",
			title: "Mobile-first émotionnel",
			desc: "L'expérience mobile a été pensée pour favoriser la navigation intuitive et l'impulse buying — des transitions fluides, des visuels plein écran et un tunnel d'achat réduit à l'essentiel.",
		},
		{
			icon: "🔍",
			title: "SEO par univers",
			desc: "Architecture SEO structurée autour des univers et des intentions de recherche : pages catégories optimisées, maillage interne fort et balisage schema.org Product pour chaque référence.",
		},
		{
			icon: "💡",
			title: "Recommandations intelligentes",
			desc: "Système de recommandations contextuelles basé sur l'univers consulté et les produits vus — pour allonger la session et augmenter le panier moyen naturellement.",
		},
		{
			icon: "🛒",
			title: "Tunnel d'achat optimisé",
			desc: "Réduction des étapes de commande, récapitulatif visuel rassurant, options de livraison claires et micro-interactions qui renforcent la confiance à chaque étape.",
		},
	];

	const universes = [
		{
			name: "Soin & beauté",
			emoji: "✨",
			color: "bg-rose-50 border-rose-100 text-rose-700",
		},
		{
			name: "Relaxation",
			emoji: "🛁",
			color: "bg-lavender-50 border-purple-100 text-purple-700",
		},
		{
			name: "Énergie & vitalité",
			emoji: "⚡",
			color: "bg-amber-50 border-amber-100 text-amber-700",
		},
		{
			name: "Équilibre & bien-être",
			emoji: "🌿",
			color: "bg-green-50 border-green-100 text-green-700",
		},
		{
			name: "Rituel quotidien",
			emoji: "☀️",
			color: "bg-orange-50 border-orange-100 text-orange-700",
		},
		{
			name: "Corps & esprit",
			emoji: "🧘",
			color: "bg-teal-50 border-teal-100 text-teal-700",
		},
	];

	const results = [
		{
			icon: "🛒",
			metric: "+55%",
			label: "Taux de conversion",
			color: "text-rose-600",
			bg: "from-rose-50 to-pink-50",
			border: "border-rose-100",
		},
		{
			icon: "⏱️",
			metric: "+70%",
			label: "Temps sur le site",
			color: "text-purple-600",
			bg: "from-purple-50 to-violet-50",
			border: "border-purple-100",
		},
		{
			icon: "📉",
			metric: "-38%",
			label: "Taux d'abandon panier",
			color: "text-emerald-600",
			bg: "from-emerald-50 to-green-50",
			border: "border-emerald-100",
		},
		{
			icon: "🔎",
			metric: "+90%",
			label: "Trafic SEO catégories",
			color: "text-amber-600",
			bg: "from-amber-50 to-yellow-50",
			border: "border-amber-100",
		},
	];

	return (
		<>
			<Helmet>
				<title>{titlePage}</title>
				<meta name="description" content={description} />
				<link rel="canonical" href={url} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={titlePage} />
				<meta property="og:description" content={description} />
				<meta property="og:url" content={url} />
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "CreativeWork",
						name: "Baume Shop — Boutique e-commerce bien-être féminin",
						description,
						url,
						dateCreated: "2024",
						provider: {
							"@type": "Organization",
							name: "Weblax",
							url: "https://weblax.fr",
						},
						about: {
							"@type": "WebSite",
							name: "Baume Shop",
							description:
								"Boutique en ligne bien-être féminin structurée par univers thématiques",
						},
					})}
				</script>
			</Helmet>

			{/* ── Hero ── */}
			<section className="relative overflow-hidden bg-gradient-to-br from-rose-50 via-white to-purple-50 border-b border-border">
				<div
					className="absolute inset-0 opacity-20"
					style={{
						backgroundImage:
							"radial-gradient(circle at 10% 60%, #fce7f3 0%, transparent 50%), radial-gradient(circle at 85% 25%, #ede9fe 0%, transparent 50%)",
					}}
				/>
				<div className="weblax-container py-20 relative">
					<div className="flex flex-wrap items-center gap-3 mb-6">
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-rose-100 text-rose-700 border border-rose-200">
							🌿 Bien-être féminin
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
							🛒 E-commerce
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-green-100 text-green-700 border border-green-200">
							✅ Projet livré
						</span>
					</div>

					<h1 className="weblax-heading-display mb-6 max-w-4xl">
						Baume Shop — L'e-commerce bien-être organisé par{" "}
						<span className="text-rose-500">univers émotionnels</span>
					</h1>

					<p className="text-lg text-muted-foreground max-w-3xl leading-relaxed mb-10">
						Weblax a entièrement repensé la boutique e-commerce Baume Shop pour
						transformer un catalogue produit complexe en une expérience d'achat
						guidée, intuitive et émotionnellement cohérente — organisée autour
						d'univers thématiques qui parlent aux femmes d'abord, aux produits
						ensuite.
					</p>

					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						{results.map((r) => (
							<div
								key={r.label}
								className="bg-white/80 backdrop-blur border border-border rounded-xl p-5 text-center shadow-sm"
							>
								<div className="text-2xl mb-1">{r.icon}</div>
								<div className={`text-3xl font-bold mb-1 ${r.color}`}>
									{r.metric}
								</div>
								<div className="text-xs text-muted-foreground">{r.label}</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<main className="weblax-container py-20 space-y-24">
				{/* ── Contexte ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Contexte
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Un catalogue riche, mais une expérience confuse
					</h2>
					<div className="grid md:grid-cols-2 gap-10">
						<div className="space-y-4 text-muted-foreground leading-relaxed">
							<p>
								Baume Shop propose une offre large de produits bien-être
								féminins — soins, compléments, accessoires de relaxation,
								rituels beauté. Un catalogue riche, mais dont la complexité
								devenait un frein à l'achat.
							</p>
							<p>
								Sans architecture claire, les utilisatrices se retrouvaient
								noyées dans un catalogue sans fil conducteur. La navigation par
								catégorie classique (soin, corps, visage…) ne reflétait pas la
								façon dont les femmes pensent leurs besoins bien-être — elles ne
								cherchent pas une crème, elles cherchent à se sentir mieux après
								une longue journée.
							</p>
							<p>
								L'insight clé :{" "}
								<strong>
									l'achat bien-être est émotionnel avant d'être rationnel
								</strong>
								. La structure du site devait refléter cette réalité, pas s'y
								opposer.
							</p>
						</div>
						<div className="space-y-3">
							<div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
								Problèmes identifiés
							</div>
							{[
								{
									issue: "Navigation par catégorie froide et peu engageante",
									impact: "Taux de rebond élevé",
								},
								{
									issue: "Absence de logique de découverte produit",
									impact: "Panier moyen faible",
								},
								{
									issue: "Aucun fil émotionnel entre les produits",
									impact: "Sessions courtes",
								},
								{
									issue: "Tunnel d'achat trop long et peu rassurant",
									impact: "Abandon panier >70%",
								},
								{
									issue: "SEO faible sur les requêtes bien-être ciblées",
									impact: "Trafic insuffisant",
								},
							].map((item) => (
								<div
									key={item.issue}
									className="p-4 rounded-xl border border-border bg-muted/30 flex items-start justify-between gap-4"
								>
									<div>
										<div className="text-sm text-foreground mb-1">
											{item.issue}
										</div>
										<div className="text-xs text-red-500 font-medium">
											{item.impact}
										</div>
									</div>
									<span className="text-red-400 shrink-0">✗</span>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* ── Univers ── */}
				<section className="bg-gradient-to-r from-rose-50 to-purple-50 border border-rose-100 rounded-2xl p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Innovation UX
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-4">
						La navigation par univers : l'idée centrale
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-8 leading-relaxed">
						Au lieu d'organiser le catalogue par type de produit, nous avons
						structuré l'expérience autour des{" "}
						<strong>intentions et des états émotionnels</strong> des
						utilisatrices. Chaque univers est une porte d'entrée vers un moment
						de vie spécifique.
					</p>
					<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{universes.map((u) => (
							<div
								key={u.name}
								className={`flex items-center gap-3 p-4 rounded-xl border bg-white shadow-sm`}
							>
								<span className="text-2xl">{u.emoji}</span>
								<span className={`text-sm font-semibold`}>{u.name}</span>
							</div>
						))}
					</div>
					<p className="text-sm text-muted-foreground mt-6 italic">
						Chaque univers possède sa propre palette, sa typographie secondaire
						et son fil narratif — pour que l'utilisatrice ressente immédiatement
						si elle est au bon endroit.
					</p>
				</section>

				{/* ── Fonctionnalités ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Solution
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Une refonte complète centrée sur l'émotion et la conversion
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Chaque décision de design et d'architecture a été validée à travers
						un prisme unique : est-ce que ça aide l'utilisatrice à trouver ce
						dont elle a besoin ? Est-ce que ça lui donne envie de rester,
						d'explorer, d'acheter ?
					</p>
					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
						{features.map((f) => (
							<div
								key={f.title}
								className="group p-6 rounded-2xl border border-border hover:border-rose-200 hover:shadow-md transition-all duration-200 bg-white"
							>
								<span className="text-3xl mb-4 block">{f.icon}</span>
								<h3 className="font-semibold text-foreground mb-2">
									{f.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{f.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Process ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Méthodologie
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-10">
						De l'analyse au lancement
					</h2>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								step: "01",
								title: "Audit UX & recherche utilisateur",
								desc: "Analyse du site existant, sessions de tests utilisateurs, heatmaps et identification des points de friction dans le parcours d'achat actuel.",
								tag: "Semaine 1",
							},
							{
								step: "02",
								title: "Architecture des univers",
								desc: "Co-construction avec la marque des univers thématiques, définition des territoires visuels et narrative de chaque univers, validation avec un panel d'utilisatrices.",
								tag: "Semaine 2",
							},
							{
								step: "03",
								title: "Design & direction artistique",
								desc: "Création du design system modulaire, déclinaison graphique par univers, maquettes interactives et validation client avant développement.",
								tag: "Semaines 3-4",
							},
							{
								step: "04",
								title: "Développement & SEO",
								desc: "Développement React, intégration des univers, optimisation des performances, balisage SEO des pages catégories et fiches produits.",
								tag: "Semaines 5-7",
							},
							{
								step: "05",
								title: "Tests & optimisation",
								desc: "Tests A/B sur les pages clés, optimisation du tunnel d'achat, corrections UX et déploiement progressif avec suivi des KPIs.",
								tag: "Semaine 8",
							},
						].map((item) => (
							<div
								key={item.step}
								className="p-6 rounded-2xl border border-border bg-white"
							>
								<div className="flex items-center justify-between mb-3">
									<span className="text-2xl font-bold text-rose-200">
										{item.step}
									</span>
									<span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
										{item.tag}
									</span>
								</div>
								<h3 className="font-semibold text-foreground mb-2">
									{item.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{item.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Stack ── */}
				<section className="bg-muted/30 rounded-2xl border border-border p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Stack technique
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">Technologies du projet</h2>
					<div className="flex flex-wrap gap-3">
						{[
							{ name: "React", desc: "Interface e-commerce" },
							{ name: "Next.js", desc: "SSR & performances" },
							{ name: "Tailwind CSS", desc: "Design system modulaire" },
							{ name: "Schema.org", desc: "Balisage Product" },
							{ name: "Algolia", desc: "Recherche produit" },
							{ name: "Stripe", desc: "Paiement sécurisé" },
							{ name: "Vercel", desc: "Déploiement edge" },
						].map((tech) => (
							<div
								key={tech.name}
								className="flex items-center gap-2 bg-white border border-border rounded-lg px-4 py-2.5 shadow-sm"
							>
								<span className="font-semibold text-sm text-foreground">
									{tech.name}
								</span>
								<span className="text-xs text-muted-foreground">
									— {tech.desc}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Résultats ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Impact
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Des chiffres qui reflètent une vraie transformation
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Six semaines après le lancement du nouveau site, les indicateurs
						confirment que repenser l'architecture autour des univers
						émotionnels a eu un impact direct et mesurable sur la performance
						commerciale de Baume Shop.
					</p>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								metric: "+55%",
								detail: "de taux de conversion",
								desc: "Résultat direct d'un parcours d'achat simplifié, d'une présentation produit émotionnelle et d'un tunnel de commande optimisé.",
								color: "text-rose-600",
								bg: "from-rose-50 to-pink-50",
								border: "border-rose-100",
							},
							{
								metric: "+70%",
								detail: "de temps passé sur le site",
								desc: "La navigation par univers incite naturellement à explorer, découvrir et s'attarder — ce que les catégories classiques ne permettaient pas.",
								color: "text-purple-600",
								bg: "from-purple-50 to-violet-50",
								border: "border-purple-100",
							},
							{
								metric: "-38%",
								detail: "d'abandons de panier",
								desc: "Un tunnel d'achat raccourci, des éléments de réassurance bien placés et des micro-interactions confiantes ont nettement amélioré la finalisation des commandes.",
								color: "text-emerald-600",
								bg: "from-emerald-50 to-green-50",
								border: "border-emerald-100",
							},
							{
								metric: "+90%",
								detail: "de trafic SEO sur les catégories",
								desc: "Les pages univers, optimisées pour les requêtes bien-être féminines à forte intention, captent désormais un trafic organique significatif.",
								color: "text-amber-600",
								bg: "from-amber-50 to-yellow-50",
								border: "border-amber-100",
							},
						].map((r) => (
							<div
								key={r.metric}
								className={`bg-gradient-to-br ${r.bg} border ${r.border} rounded-2xl p-8`}
							>
								<div className={`text-5xl font-bold mb-2 ${r.color}`}>
									{r.metric}
								</div>
								<div className="font-semibold text-foreground mb-3">
									{r.detail}
								</div>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{r.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Citation ── */}
				<section className="border border-rose-100 bg-gradient-to-br from-rose-50 to-purple-50 rounded-2xl p-10 text-center">
					<span className="text-4xl mb-6 block">🌿</span>
					<blockquote className="text-xl font-medium text-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
						"L'idée des univers a tout changé. Les clientes ne nous disent plus
						qu'elles ont du mal à trouver — elles nous disent qu'elles ont du
						mal à choisir tellement elles aiment tout."
					</blockquote>
					<cite className="text-sm text-muted-foreground not-italic">
						Fondatrice Baume Shop
					</cite>
				</section>

				{/* ── Conclusion + CTA ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-rose-400" />
						<span className="text-xs font-semibold text-rose-600 uppercase tracking-widest">
							Conclusion
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						La structure d'un catalogue, c'est une décision stratégique
					</h2>
					<div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed mb-12">
						<p>
							Baume Shop démontre qu'un catalogue bien structuré n'est pas
							qu'une question d'organisation — c'est un levier de conversion
							puissant. En alignant l'architecture du site avec la façon dont
							les utilisatrices pensent et ressentent leurs besoins, Weblax a
							transformé une boutique confuse en une destination bien-être
							cohérente.
						</p>
						<p>
							La navigation par univers émotionnels est une approche
							particulièrement adaptée aux marques lifestyle et bien-être, où
							l'achat est guidé par l'envie et l'aspiration autant que par le
							besoin rationnel.
						</p>
						<p>
							Ce projet confirme une conviction forte chez Weblax : la meilleure
							stratégie UX e-commerce ne part pas des produits, mais des
							personnes qui vont les acheter.
						</p>
					</div>

					<div className="bg-foreground text-background rounded-2xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
						<div>
							<h3 className="text-xl font-bold mb-2">
								Votre boutique e-commerce mérite une vraie stratégie
							</h3>
							<p className="text-sm opacity-70">
								Architecture produit, UX conversion, SEO e-commerce — parlons de
								votre projet.
							</p>
						</div>
						<a
							href="/contact"
							className="shrink-0 bg-white text-foreground font-semibold px-8 py-3 rounded-xl hover:bg-rose-50 transition-colors text-sm whitespace-nowrap"
						>
							Démarrer un projet →
						</a>
					</div>
				</section>
			</main>
		</>
	);
}
